import { CloseOutlined } from '@ant-design/icons';
import {
  Badge,
  Button,
  Input,
  Modal,
  Popover,
  Progress,
  Radio,
  Tooltip
} from 'antd';

import { capitalize, find, toLower } from '../../../../util/lodashlike';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  getCRSRiskScoreHistory,
  putRiskScoreAcceptance,
  updateCRSRiskScoreHistory
} from '../../../../redux/actions/crs/crsRiskDetail';
import { getInDecimal } from '../../../../services/util';
import { hasResource } from '../../../../util';
import { normalizeAlertScore } from '../../../../util/normalization';
import { CRS_CONSTANTS } from '../../../constants/crsContants';
import ScoreHistoryTable from '../../components/ScoreHistoryTable';
import styles from './index.scss';
import {
  isValidReason,
  ScoringPanelCorporateConfig,
  ScoringPanelIndividualConfig
} from './util';
import useAlertLevelConfigs from '../../../common/hooks/useAlertLevelConfig';
import { fetchAMLSAlertLevel } from '../../../../redux/actions/common';
import { getSelectedResources } from '../../../../util/resourceManager';

const { GET_DATA } = CRS_CONSTANTS;
const { crsResources } = getSelectedResources();

const convertYearsToCamelCase = (onBoardDate) => {
  const stringDate = moment(onBoardDate).fromNow(true);

  if (stringDate && stringDate.includes('year')) {
    return stringDate.replace('year', 'Year');
  }

  return '-';
};

export const ScoreBar = ({
  score,
  riskRating,
  riskRatingColor,
  ratingType = ''
}) => {
  let alertScore = getInDecimal(score);
  return (
    <div style={{ display: 'inline-flex' }}>
      <div style={{ width: '80px', marginRight: '8px' }}>
        <Progress
          percent={normalizeAlertScore(alertScore)}
          size="small"
          strokeWidth="12px"
          showInfo={false}
          strokeColor={riskRatingColor}
        />
      </div>
      {ratingType !== 'manual' ? (
        <Tooltip title={`${normalizeAlertScore(alertScore)}/100`}>
          <div>
            {riskRating || ''}
            <span style={{ marginLeft: '4px' }}>
              {normalizeAlertScore(alertScore)}
              <span className={styles.greyed}>/100</span>
            </span>
          </div>
        </Tooltip>
      ) : (
        <div>{riskRating || ''}</div>
      )}
    </div>
  );
};

export const ScoringPanel = ({
  data = {},
  authentication,
  dispatch,
  getRiskScoreHistoryResponse,
  updateRiskScoreHistoryResponse,
  acceptRiskScoreResponse,
  isEmbedded,
  alertLevels
}) => {
  const [changeScoreModalVisible, setChangeScoreModalVisible] = useState(false);
  const [acceptScoreModalVisible, setAcceptScoreModalVisible] = useState(false);
  const [scoreHistoryVisible, setScoreHistoryVisible] = useState(false);
  const [acceptScoreDisabled, setAcceptScoreDisabled] = useState(false);
  const [newRiskLevel, setNewRiskLevel] = useState('');
  const [reason, setReason] = useState('');
  const { crsAlertLevels, getCRSLevelColor, getCRSLevelLabel } =
    useAlertLevelConfigs(alertLevels);

  const ScoringPanelConfig =
    data.customerType === 'C'
      ? ScoringPanelCorporateConfig
      : ScoringPanelIndividualConfig;

  const handleChangeScoreModalApply = () => {
    dispatch(
      updateCRSRiskScoreHistory(
        {
          customerKey: decodeURIComponent(data.customer_id),
          changeFrom: data.riskRating,
          changeTo: newRiskLevel,
          reason: reason.trim()
        },
        GET_DATA
      )
    );
  };

  const handleAcceptScoreModalApply = () => {
    dispatch(
      putRiskScoreAcceptance(
        {
          customerKey: data.customer_id,
          riskRating: data.riskRating,
          notes: reason
        },
        GET_DATA
      )
    );
  };

  const handleChangeScoreModalCancel = () => {
    setChangeScoreModalVisible(false);
    setNewRiskLevel('');
    setReason('');
  };

  const handleAcceptScoreModalCancel = () => {
    setAcceptScoreModalVisible(false);
    setReason('');
  };

  const onRiskLevelChange = (e) => {
    setNewRiskLevel(e.target.value);
  };

  const onKeyDown = useCallback((event) => {
    if (event.keyCode === 27) {
      // when esc is pressed
      setScoreHistoryVisible(false);
    }
  }, []);

  const isInvalidScore = (value) => {
    return (
      value === undefined || value === null || value === '' || isNaN(value)
    );
  };

  const renderData = (key) => {
    const score = data['customerRiskScore'];
    //Keeping the score as same and showing  system and manual risk ratings.
    switch (key) {
      case 'customerRiskScore':
        if (isInvalidScore(score)) return <span>-</span>;
        return (
          <ScoreBar
            score={score}
            riskRating={
              data['riskRatingLabel'] || capitalize(data['riskRating'])
            }
            riskRatingColor={data['riskRatingColor']}
          />
        );

      case 'manualRiskRating':
        if (isInvalidScore(score) || !data['manualRiskRating'])
          return <span>-</span>;
        return (
          <ScoreBar
            score={score}
            riskRating={
              data['manualRiskRating'] || capitalize(data['manualRiskRating'])
            }
            riskRatingColor={data['manualRiskRatingColor']}
            ratingType="manual"
          />
        );

      case 'riskScoreHistory':
        return (
          <Badge
            size="small"
            count={getRiskScoreHistoryResponse?.data?.count}
            offset={[-8, 0]}
            style={{ backgroundColor: '#21B4BD' }}>
            <Popover
              placement="bottomLeft"
              title={
                <>
                  History of Risk Score Changes
                  <Button
                    size="small"
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={() => setScoreHistoryVisible(false)}
                  />
                </>
              }
              content={
                <ScoreHistoryTable customerKey={data?.customer_id || ''} />
              }
              trigger="click"
              visible={scoreHistoryVisible}
              destroyTooltipOnHide
              onVisibleChange={(visible) => setScoreHistoryVisible(visible)}>
              <span className={styles.historyIcon} role="button" tabIndex="0" />
            </Popover>
          </Badge>
        );

      case 'customerOnboardDate':
        return convertYearsToCamelCase(data[key]);

      case 'customer_id':
        return decodeURIComponent(data[key]);

      default: {
        const value = data[key];
        return value ?? '-';
      }
    }
  };

  const getRiskScoreHistory = () => {
    dispatch(
      getCRSRiskScoreHistory(
        { customerKey: data.customer_id, limit: 10, offset: 0 },
        GET_DATA
      )
    );
  };

  useEffect(() => {
    if (updateRiskScoreHistoryResponse?.fetched) {
      setChangeScoreModalVisible(false);
      setNewRiskLevel('');
      setReason('');
      // get history to show the count badge in history icon.
      getRiskScoreHistory();
    }
  }, [updateRiskScoreHistoryResponse]);

  useEffect(() => {
    if (acceptRiskScoreResponse?.fetched) {
      setAcceptScoreModalVisible(false);
      setReason('');
      setAcceptScoreDisabled(true);
      // get history to show the count badge in history icon.
      getRiskScoreHistory();
    }
  }, [acceptRiskScoreResponse]);

  useEffect(() => {
    if (!alertLevels?.length) {
      dispatch(fetchAMLSAlertLevel());
    }

    document.addEventListener('keydown', onKeyDown, false);
    return () => {
      document.removeEventListener('keydown', onKeyDown, false);
    };
  }, []);

  useEffect(() => {
    // Get the history details to show the count badge in the history icon
    if (data.customer_id) {
      getRiskScoreHistory();
    }
  }, [data]);

  return (
    <>
      <div className={styles.actionHeader}>
        {!isEmbedded && <h3>Customer Details</h3>}
        {!isEmbedded &&
          hasResource(
            authentication?.data,
            crsResources.APP_3_RESOURCE_UI_CRS_CHANGE_OR_ACCEPT_RISK_SCORE
          ) && (
            <Button onClick={() => setChangeScoreModalVisible(true)}>
              Change Risk Level
            </Button>
          )}
        {!isEmbedded &&
          hasResource(
            authentication?.data,
            crsResources.APP_3_RESOURCE_UI_CRS_CHANGE_OR_ACCEPT_RISK_SCORE
          ) && (
            <Button
              type="primary"
              disabled={data['isReviewed'] || acceptScoreDisabled}
              onClick={() => setAcceptScoreModalVisible(true)}>
              Accept Risk Level
            </Button>
          )}
        <Modal
          className={styles.modal}
          style={{ top: 203 }}
          title="Change Risk Level"
          visible={changeScoreModalVisible}
          okText="Apply"
          onOk={handleChangeScoreModalApply}
          okButtonProps={{
            disabled: !isValidReason(reason) || !newRiskLevel,
            loading: updateRiskScoreHistoryResponse?.isFetching
          }}
          onCancel={handleChangeScoreModalCancel}>
          <div className={styles.scoreRow}>
            <div>
              <div className={styles.label}>Current Customer Risk Score</div>
              <ScoreBar
                score={getInDecimal(data['customerRiskScore'])}
                riskRating={
                  data['riskRatingLabel'] || capitalize(data['riskRating'])
                }
                riskRatingColor={data['riskRatingColor']}
              />
            </div>
            <div>
              <div className={styles.label}>New Customer Risk Level</div>
              <Radio.Group
                size="small"
                value={newRiskLevel}
                onChange={onRiskLevelChange}>
                {crsAlertLevels?.map(({ code, displayName, color }) => (
                  <Radio.Button
                    key={code}
                    value={code}
                    disabled={toLower(data.riskRating) === toLower(code)}>
                    <div
                      className={styles.importanceIndicator}
                      style={{ background: color }}
                    />
                    {displayName || capitalize(code)}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </div>
          </div>
          <div>
            <div className={styles.label}>
              Reasons for Change<span style={{ color: 'red' }}>*</span>
            </div>
            <Input.TextArea
              rows="5"
              placeholder="Reasons for Changing Risk Level"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </Modal>
        <Modal
          className={styles.modal}
          style={{ top: 203 }}
          title="Accept Risk Score"
          visible={acceptScoreModalVisible}
          okText="Apply"
          onOk={handleAcceptScoreModalApply}
          okButtonProps={{
            disabled: !isValidReason(reason),
            loading: acceptRiskScoreResponse?.isFetching
          }}
          onCancel={handleAcceptScoreModalCancel}>
          <div>
            <div className={styles.label}>Reasons for Accept</div>
            <Input.TextArea
              rows="5"
              placeholder="Reasons for Accepting Risk Score"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </Modal>
      </div>
      <div className={styles.itemsContainer}>
        <div className={styles.item}>
          <div className={styles.userIcon}>
            <em className="fa fa-user" />
          </div>
        </div>
        {ScoringPanelConfig.map((col) => {
          return (
            <div className={styles.item} key={col.item}>
              <div className={styles.label}>{col.label}</div>
              <div className={styles.value}>{renderData(col.item, data)}</div>
            </div>
          );
        })}
      </div>
    </>
  );
};

function mapStateToProps(state, ownProps = {}) {
  const {
    updateRiskScoreHistoryResponse,
    getRiskScoreHistoryResponse,
    acceptRiskScoreResponse
  } = state.crsRiskDetailReducer;
  return {
    updateRiskScoreHistoryResponse,
    getRiskScoreHistoryResponse,
    acceptRiskScoreResponse,
    authentication: state.authentication,
    alertLevels: state.amlsCommon.alertLevelConfigs?.data
  };
}

function mapDispatchToProps(dispatch, ownProps = {}) {
  return {
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ScoringPanel);
